import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import { DialogTitle } from "@mui/material";

import { FaPlus, FaTrash } from "react-icons/fa";
import { Formik } from "formik";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import VuiAutocomplete from "components/AutoComplete";

import { IntegrationSchema } from "validations";
import useCommon from "hooks/useCommon";
import { updateIntegration } from "store/slice/integrationsSlice";
import DynamicFieldBuilder from "../utils/DynamicFeildBuilder";
import OverlayLoader from "components/OverlayLoader";

const EMPTY_CREDENTIAL_FIELD = {
  name: "",
  label: "",
  type: "string",
  required: false,
  secret: false,
  placeholder: "",
  helpText: "",
  options: [],
};

const FIELD_TYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "password", label: "Password" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "json", label: "JSON" },
];

const EditIntegrationDialog = ({ open, onClose, integration, onSubmit }) => {
  const { STORE } = useCommon();
  const { updateLoading } = STORE((state) => state.integrations);

  if (!integration) return null;

  const handleSubmit = (values) => {
    onSubmit?.(values);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <VuiTypography variant="button" fontWeight="bold">
          Edit Integration
        </VuiTypography>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={{
          key: integration.key,
          displayName: integration.displayName || "",
          description: integration.description || "",
          logoUrl: integration.logoUrl || "",
          learnMore: integration.learnMore || "",
          status: integration.status || "enabled",
          supportsWebhooks: integration.supportsWebhooks ?? true,
          supportsPolling: integration.supportsPolling ?? false,
          supportsPropertyDiscovery: integration.supportsPropertyDiscovery ?? false,

          credentialFields: integration.credentialFields || [],
          mappingFields: integration.mappingFields || [],
          defaultWebhookEvents: integration.defaultWebhookEvents || [],
        }}
        validationSchema={IntegrationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => {
          const addCredentialField = () => {
            setFieldValue("credentialFields", [
              ...values.credentialFields,
              { ...EMPTY_CREDENTIAL_FIELD },
            ]);
          };

          const updateCredentialField = (index, field, value) => {
            setFieldValue(`credentialFields.${index}.${field}`, value);
          };

          const removeCredentialField = (index) => {
            const updated = [...values.credentialFields];
            updated.splice(index, 1);
            setFieldValue("credentialFields", updated);
          };

          return (
            <>
              <DialogContent>
                <OverlayLoader open={updateLoading} message="Saving changes..." />
                {/* BASIC INFO */}
                <Grid container columnSpacing={2} rowSpacing={0}>
                  <Grid item xs={12} md={6}>
                    <VuiTypography variant="caption">Provider Key *</VuiTypography>
                    <VuiInput gradient fullWidth disabled value={values.key} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <VuiTypography variant="caption">Display Name *</VuiTypography>
                    <VuiInput
                      gradient
                      fullWidth
                      name="displayName"
                      value={values.displayName}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <VuiTypography variant="caption">Description</VuiTypography>
                    <VuiInput
                      gradient
                      fullWidth
                      multiline
                      rows={2}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <VuiTypography variant="caption">Logo URL</VuiTypography>
                    <VuiInput
                      gradient
                      fullWidth
                      name="logoUrl"
                      value={values.logoUrl}
                      onChange={handleChange}
                      inputProps={{ maxLength: 800 }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <VuiTypography variant="caption">Learn More URL</VuiTypography>

                  <VuiInput
                    gradient
                    fullWidth
                    placeholder="https://developers.mews.com/"
                    name="learnMore"
                    value={values.learnMore}
                    onChange={handleChange}
                    inputProps={{ maxLength: 800 }}
                  />

                  <VuiTypography variant="caption" opacity={0.6} mt={0.5} display="block">
                    Optional provider documentation or onboarding URL.
                  </VuiTypography>

                  {touched.learnMore && errors.learnMore && (
                    <VuiTypography variant="caption" color="error">
                      {errors.learnMore}
                    </VuiTypography>
                  )}
                </Grid>

                {/* CREDENTIAL FIELDS */}
                <DynamicFieldBuilder
                  title="Credential Fields"
                  description="Define credentials required when a hotel configures this integration."
                  fields={values.credentialFields}
                  fieldKey="credentialFields"
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                />

                {/* MAPPING FIELDS */}

                <DynamicFieldBuilder
                  title="Mapping Fields"
                  description="Define fields used to map external provider data."
                  fields={values.mappingFields}
                  fieldKey="mappingFields"
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                />

                {/* CAPABILITIES */}
                <VuiBox mt={1}>
                  <VuiTypography variant="button">Capabilities</VuiTypography>

                  <VuiBox display="flex" gap={2} width="100%" mt={2}>
                    {[
                      ["supportsWebhooks", "Webhooks"],
                      ["supportsPolling", "Polling"],
                      ["supportsPropertyDiscovery", "Property Discovery"],
                    ].map(([key, label]) => (
                      <VuiBox key={key} display="flex" alignItems="center" gap={1} flex={1}>
                        <VuiSwitch
                          color="success"
                          checked={values[key]}
                          onChange={() => setFieldValue(key, !values[key])}
                        />
                        <VuiTypography variant="caption">{label}</VuiTypography>
                      </VuiBox>
                    ))}
                  </VuiBox>
                </VuiBox>
              </DialogContent>

              <DialogActions>
                <VuiButton variant="outlined" color="primary" onClick={onClose}>
                  Cancel
                </VuiButton>

                <VuiButton color="primary" onClick={handleSubmit} disabled={updateLoading}>
                  {updateLoading ? "Saving..." : "Save Changes"}
                </VuiButton>
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default EditIntegrationDialog;
